import { FC, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Head from 'next/head';
import { Button, Center, Icon, LottieAnimation, Page } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { SummaryItem } from 'src/api/learningSessions/types';
import soundFile from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/points-update.mp3';
import { defaultAnimationOptions } from 'src/components/constants';
import sessionCompletedAnimation from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/session-completed-animation.json';
import { WordRowList } from 'src/apps/Generic/WordRowList';
import { useBackCta } from 'src/hooks';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { PathBeforeNavigate, PathContinueButtons } from 'src/apps/Path/PathContinueButtons';
import * as S from './classicStyles';

const msgs = defineMessages({
  title: { id: 'scenario_end_of_session_title' },
  learningSettings: { id: 'web_ls_menu' },
  lessonCompleted: { id: 'learnq_eos_sentence_title' },
  lessonSummary: { id: 'learnq_eosCta_lessonSummary' },
  wordsAndPhrasesTotal: { id: 'scenario_words_and_phrases_total' },
  continue: { id: 'Continue' },
});

/**
 * Pre-redesign learn EOS: completion sound, Lottie, and the per-word summary.
 * Shown to 2025 learners who are not on Path.
 */
export const ClassicEndOfSessionLearn: FC<{
  summaryItems: SummaryItem[];
  pathwayNodeId?: number | null;
  /** Called with whichever exit action ("Continue" or, when launched from Path, "Go back to
   * my path") the learner picks, letting the caller defer it behind an interstitial instead
   * of running it right away. When omitted, both exits run immediately. */
  onBeforeExit?: PathBeforeNavigate;
}> = ({ summaryItems, pathwayNodeId = null, onBeforeExit }) => {
  const { safePush } = useSafePush();
  const intl = useIntl();
  const backCta = useBackCta();
  const [isContinuing, setIsContinuing] = useState(false);
  const hasContinuedRef = useRef(false);

  const handleContinue = () => {
    if (hasContinuedRef.current) {
      return;
    }
    hasContinuedRef.current = true;

    const doContinue = () => {
      setIsContinuing(true);
      void Promise.resolve(safePush(backCta.href)).catch(() => {
        void safePush(backCta.href, true);
      });
    };

    if (onBeforeExit) {
      onBeforeExit(doContinue, 'continue');
    } else {
      doContinue();
    }
  };

  return (
    <Page footerStyle="none">
      <Head>
        <title>{intl.formatMessage(msgs.title)} - Memrise</title>
      </Head>

      <S.Root data-testid="scenarios-EndOfSession">
        <S.Content>
          <Center $centerAlignItems>
            <S.Header>{intl.formatMessage(msgs.lessonCompleted)}</S.Header>

            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio autoPlay preload="auto" src={soundFile} data-testid="session-completed-sound" />
            <S.AnimationContainer data-testid="session-completed-animation">
              <LottieAnimation
                {...defaultAnimationOptions}
                animationData={sessionCompletedAnimation}
                loop
              />
            </S.AnimationContainer>

            <S.ContentWrapper $align="center">
              <S.ButtonContainer>
                {pathwayNodeId !== null ? (
                  <PathContinueButtons
                    languagePairId={getLanguagePairId()}
                    pathwayNodeId={pathwayNodeId}
                    onBeforeNavigate={onBeforeExit}
                  />
                ) : (
                  <Button
                    buttonType="primary"
                    buttonSize="large"
                    wide
                    loading={isContinuing}
                    disabled={isContinuing}
                    onClick={handleContinue}
                    data-testid="learn-continue-button"
                  >
                    {intl.formatMessage(msgs.continue)}
                  </Button>
                )}
              </S.ButtonContainer>

              <S.SummaryTitle>{intl.formatMessage(msgs.lessonSummary)}</S.SummaryTitle>
              <S.SummaryDescription>
                {intl.formatMessage(msgs.wordsAndPhrasesTotal, { itemsTotal: summaryItems.length })}
              </S.SummaryDescription>

              <S.ListWrapper>
                <WordRowList
                  items={summaryItems.map(i => ({
                    id: i.learnableId,
                    sourceValue: i.definition.value,
                    targetValue: i.item.value,
                  }))}
                />
              </S.ListWrapper>
            </S.ContentWrapper>
          </Center>
        </S.Content>
        <S.SettingsIconWrapper
          onClick={async () => {
            await safePush(ROUTES.settings.learning);
          }}
          aria-label={intl.formatMessage(msgs.learningSettings)}
        >
          <Icon name="settingsFilled" />
        </S.SettingsIconWrapper>
      </S.Root>
    </Page>
  );
};
